// D-038 section bar for a sidebar group (owner, 2026-10-21): on every page of the group, a row of
// links to the group's other pages, like the Reports switcher (Sales · Money · Operations). Built
// from the sidebar's own items, labels and icons, so the two never disagree. The current page gets
// the brand tint with a gradient icon (D-039). Groups opt in through SECTION_BARS.
import { useLocation } from "@tanstack/react-router";
import { t } from "@ady/i18n";
import { cx, Icon } from "@ady/ui";
import { ReportsSwitcher, type Section } from "../analytics/ReportsPage";
import { AiButton } from "./AiButton";
import { AppLink } from "./AppLink";
import { useShell } from "./context";
import { hrefFor, iconFor, itemForPathname, NAV, visibleNav } from "./nav";

/** Per group (owner, 2026-10-21): the items the sidebar keeps under the group, the items it lifts
 *  out as their own top-level links, and the pages shown as tabs on the page. A group with no
 *  sidebar items is a single sidebar link. Tab pages open from the bar only. */
export const SECTION_BARS: Record<
  string,
  {
    sidebar: string[];
    promote?: string[];
    tabs: string[];
    /** More tab rows in the same group, each its own bar; the hub shows each row's first page. */
    rows?: string[][];
    /** Tabs shown as an icon button at the right end of the row instead (owner, 2026-10-21). */
    iconOnly?: string[];
    /** Pages that belong to the bar (it shows on them) but get no button: opened from elsewhere. */
    hidden?: string[];
  }
> = {
  orders: {
    sidebar: [],
    tabs: ["orders", "unfinished", "returns", "codChecks"],
  },
  customers: {
    sidebar: [],
    tabs: ["customers", "segments", "reviews", "inbox"],
  },
  money: {
    sidebar: [],
    tabs: ["money", "payments", "cod", "accounts", "expenses", "suppliers", "statements"],
  },
  marketing: {
    sidebar: [],
    tabs: [
      "promotions",
      "newsletter",
      "flashDeals",
      "offerPopup",
      "links",
      "automations",
      "messages",
      "affiliates",
    ],
  },
  // Online store, Apps, Settings: one sidebar link to the overview, whose cards open each page
  // (owner, 2026-10-21; shell/GroupHub).
  store: { sidebar: ["storeHome"], tabs: [] },
  // Courier accounts and Delivery share one tab row; the Apps overview shows only the first. The
  // Developer pages are a second row (owner, 2026-10-29; D-090).
  apps: {
    sidebar: ["appsHome"],
    tabs: ["couriers", "delivery", "installation"],
    rows: [["apiKeys", "mcp", "webhooks"]],
  },
  settings: { sidebar: ["settingsHome"], tabs: [] },
  reports: {
    sidebar: [],
    tabs: ["reports", "traffic"],
  },
  products: {
    sidebar: [],
    // Media library is the last tab (owner, 2026-10-22), not a sidebar link. "More product details"
    // was removed: Digital products moved to Settings, Page design to the product row menu.
    // Categories moved from the sidebar to the tab right after Products (owner, 2026-11-05).
    promote: ["inventory"],
    // Import and export has no button here (owner, 2026-10-22); it opens from the products "⋯" menu.
    tabs: ["products", "categories", "collections", "brands", "transfers", "import", "media"],
    hidden: ["import"],
  },
};

/** The tab row a page belongs to, if any. */
export const tabRow = (bar: (typeof SECTION_BARS)[string] | undefined, id: string) =>
  bar ? [bar.tabs, ...(bar.rows ?? [])].find((r) => r.includes(id)) : undefined;

export function GroupBar() {
  const { membership, orgSlug, storeSlug } = useShell();
  const path = useLocation({ select: (l) => l.pathname });
  const current = itemForPathname(path, orgSlug, storeSlug);
  const group = current && NAV.find((g) => g.items.includes(current));
  const bar = group ? SECTION_BARS[group.id] : undefined;
  const row = current ? tabRow(bar, current.id) : undefined;
  if (!current || !group || !bar || !row) return null;
  // Reports: its own switcher (Sales / Money / Operations + Visitors and traffic), keyed by the
  // topic in the address (…/reports/{topic}; …/reports is Sales).
  if (group.id === "reports") {
    const topic = path.split("/reports/")[1]?.split("/")[0];
    return (
      <>
        <ReportsSwitcher
          current={current.id === "traffic" ? undefined : ((topic || "sales") as Section)}
        />
        {/* root strolls beside the Reports switcher on every report (owner, 2026-10-24, D-057). */}
        <span className="hidden sm:flex">
          <AiButton />
        </span>
      </>
    );
  }
  const visible = visibleNav(membership).find((g) => g.id === group.id)?.items ?? [];
  const items = row
    .map((id) => visible.find((i) => i.id === id))
    .filter((i): i is NonNullable<typeof i> => Boolean(i));
  if (items.length < 2) return null;
  const shown = items.filter((i) => !bar.hidden?.includes(i.id));
  const icons = shown.filter((i) => bar.iconOnly?.includes(i.id));
  const tabs = shown.filter((i) => !bar.iconOnly?.includes(i.id));
  // Rendered inside the shell's top row (TopRow): the bar on the left, the page's own buttons
  // in the middle slot, these icon links last.
  return (
    <>
      <nav
        aria-label={t(group.label)}
        className="ady-scroll-quiet flex w-fit max-w-full gap-1 overflow-x-auto rounded-xs border border-card bg-surface p-1"
      >
        {tabs.map((item) => {
          const on = item.id === current.id;
          return (
            <AppLink
              key={item.id}
              href={hrefFor(item, orgSlug, storeSlug)}
              aria-current={on ? "page" : undefined}
              className={cx(
                "flex min-h-control flex-none items-center rounded-xs text-sm font-medium whitespace-nowrap no-underline",
                // Marketing has eight tabs and a seated root: slightly tighter tabs keep one row.
                group.id === "marketing" ? "gap-1.5 px-2.5" : "gap-2 px-3",
                on ? "ady-bg-brand-tint" : "ady-hover-brand text-fg-tertiary",
              )}
            >
              <Icon name={iconFor(item)} size={16} gradient={on} />
              <span className={on ? "ady-text-brand" : "ady-hover-label"}>{t(item.label)}</span>
            </AppLink>
          );
        })}
      </nav>
      {/* root sits beside the page buttons on every Marketing tab (owner, 2026-10-24, D-057). */}
      {group.id === "marketing" ? (
        <span className="ady-sit-root ms-auto hidden sm:flex">
          <AiButton look="sit" />
        </span>
      ) : null}
      {icons.length ? (
        <div className="order-last flex flex-none gap-1">
          {icons.map((item) => {
            const on = item.id === current.id;
            const label = t(item.label);
            return (
              <AppLink
                key={item.id}
                href={hrefFor(item, orgSlug, storeSlug)}
                aria-label={label}
                title={label}
                aria-current={on ? "page" : undefined}
                className={cx(
                  "flex size-control items-center justify-center rounded-xs border border-card no-underline",
                  on
                    ? "ady-bg-brand-tint"
                    : "bg-surface text-fg-secondary hover:bg-hover hover:text-fg",
                )}
              >
                <Icon name={iconFor(item)} gradient={on} />
              </AppLink>
            );
          })}
        </div>
      ) : null}
    </>
  );
}
